import { useInputControl } from '@conform-to/react'
import React, { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '#app/utils/misc.tsx'
import { Button } from './ui/button.tsx'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import { Icon } from './ui/icon.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import { Textarea } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

const fieldBottomMarginClass = 'mb-4 lg:mb-5'

export function ErrorList({
  id,
  errors,
}: {
  errors?: ListOfErrors
  id?: string
}) {
  const { t } = useTranslation()
  const errorsToRender = errors?.filter(Boolean)
  if (!errorsToRender?.length) return null
  return (
    <ul id={id} className="mt-2 flex flex-col gap-1">
      {errorsToRender.map(e => {
        if (!e) return null
        return (
          <li key={e} className="text-1 text-foreground-destructive">
            {t(e as any)}
          </li>
        )
      })}
    </ul>
  )
}

export function Field({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
  inputProps: React.InputHTMLAttributes<HTMLInputElement>
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  const id = inputProps.id ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined

  return (
    <div className={cn(className, fieldBottomMarginClass)}>
      <Label htmlFor={id} className="font-normal" {...labelProps} />
      <Input
        id={id}
        className="mt-2 h-7"
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...inputProps}
        key={id}
      />
      {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
    </div>
  )
}

export function PasswordField({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
  inputProps: React.InputHTMLAttributes<HTMLInputElement>
  errors?: ListOfErrors
  className?: string
}) {
  const [showPassword, setShowPassword] = useState(false)
  const { t } = useTranslation()

  return (
    <div className={cn(className, fieldBottomMarginClass, 'relative')}>
      <Button
        aria-label={t('passwordFieldVisiblityButton')}
        className="absolute top-[2.25rem] right-3"
        size="xs"
        variant="link"
        onClick={e => {
          e.preventDefault()
          setShowPassword(prevState => !prevState)
        }}
      >
        <Icon
          className="text-3 text-mauve-11"
          name={showPassword ? 'eye-none' : 'eye-open'}
        />
      </Button>
      <Field
        labelProps={labelProps}
        inputProps={{
          ...inputProps,
          type: showPassword ? 'text' : 'password',
        }}
        errors={errors}
      />
    </div>
  )
}

export function TextareaField({
  labelProps,
  textareaProps,
  errors,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
  textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  const id = textareaProps.id ?? textareaProps.name ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined
  return (
    <div className={cn(className, fieldBottomMarginClass)}>
      <Label htmlFor={id} {...labelProps} />
      <Textarea
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...textareaProps}
      />
      {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
    </div>
  )
}

export function CheckboxField({
  labelProps,
  buttonProps,
  errors,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
  buttonProps: CheckboxProps & {
    name: string
    form: string
    value?: string
  }
  errors?: ListOfErrors
  className?: string
}) {
  const { key, defaultChecked, ...checkboxProps } = buttonProps
  const fallbackId = useId()
  const checkedValue = buttonProps.value ?? 'on'
  const input = useInputControl({
    key,
    name: buttonProps.name,
    formId: buttonProps.form,
    initialValue: defaultChecked ? checkedValue : undefined,
  })
  const id = buttonProps.id ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined
  const labelId = `label-${id}`

  return (
    <div className={cn(className, fieldBottomMarginClass)}>
      <div className="flex items-center gap-2">
        <Checkbox
          {...checkboxProps}
          id={id}
          aria-labelledby={labelId}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          checked={input.value === checkedValue}
          onCheckedChange={state => {
            input.change(state.valueOf() ? checkedValue : '')
            buttonProps.onCheckedChange?.(state)
          }}
          onFocus={event => {
            input.focus()
            buttonProps.onFocus?.(event)
          }}
          onBlur={event => {
            input.blur()
            buttonProps.onBlur?.(event)
          }}
          type="button"
        />
        <label
          id={labelId}
          htmlFor={id}
          {...labelProps}
          className="text-2 text-muted-foreground self-center"
        />
      </div>
      {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
    </div>
  )
}
